import { motion, type HTMLMotionProps } from "framer-motion";
import { useMemo } from "react";
import { useMotionPrefs } from "./MotionProvider";
import { staggerContainer, type StaggerOptions } from "./variants";
import { Reveal } from "./Reveal";

type StaggerProps = StaggerOptions &
  Omit<HTMLMotionProps<"div">, "variants" | "initial" | "whileInView" | "viewport"> & {
    as?: "div" | "ul" | "ol" | "section";
    /** Animate on mount instead of on scroll. Use above the fold. */
    onMount?: boolean;
  };

export function Stagger({
  as = "div",
  interval,
  delay,
  from,
  onMount = false,
  children,
  ...rest
}: StaggerProps) {
  const { reduce, viewport } = useMotionPrefs();
  const Tag = motion[as] as typeof motion.div;

  const variants = useMemo(
    () => staggerContainer({ interval, delay, from }, reduce),
    [interval, delay, from, reduce],
  );

  return (
    <Tag
      variants={variants}
      initial="hidden"
      {...(onMount
        ? { animate: "visible" as const }
        : { whileInView: "visible" as const, viewport })}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Children inherit "hidden" -> "visible" through variant context.
 * standalone={false} is the entire point: no initial/whileInView here.
 */
Stagger.Item = function StaggerItem(props: Parameters<typeof Reveal>[0]) {
  return <Reveal standalone={false} {...props} />;
};
